import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ELayersView from 'enums/ELayersView';
import 'components/customComponents/Layers/SelectIngredients.scss';
import { TreeNodeExpanded } from 'business/models/CustomModels';
import { treeNodeIconsColor } from 'utils/Global';
import { FragmentLayerDTO, FragmentScreenInfoDTO } from 'business/models/Models';
import WizardService from 'business/services/WizardService';
import { TreeService } from 'business/services/TreeService';
import { usePreserveExperiment, useRMSelector } from 'redux/hooks';
import { withTabBackButton, WithTabBackButtonStateProps } from 'components/tab';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { IStyle } from 'Styles';
import { InputWithIcon } from 'components/input';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import CustomBreadCrumbs from 'components/customComponents/TreeControl/CustomBreadCrumbs/CustomBreadCrumbs';
import {
  useExpId,
  useExpTreeNodeId,
  useSelectedExpLayer,
  useSelectedLayerId,
} from './hooks';
import { selectExp } from 'redux/selectors/ExperimentStateSelector';
import { useLayersContainerContext } from '../Layers/hooks';
import { getTreeNodeIdsByType } from '../Layers/utils';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { LoadingDiv } from 'components/generic';
import useBroadcast, {
  CHANNEL_ACTIONS,
  CHANNEL_TYPES,
  MessageType,
} from 'business/broadcast';

interface SelectFragmentScreenListProps extends WithTabBackButtonStateProps {
  setContentView: (contentView: ELayersView) => void;
  parentNode: any;
  handleBackToPreviousScreen: (contentView: ELayersView) => () => void;
  resetFragmentTreeStates: () => void;
  setSelectedFragmentLayer: (fragment: FragmentLayerDTO) => void;
  selectedFragmentLayer: FragmentLayerDTO;
}

const treeService = new TreeService();
const wizardService = new WizardService();

const SelectFragmentScreen = (props: SelectFragmentScreenListProps) => {
  const {
    setContentView,
    parentNode,
    handleBackToPreviousScreen,
    setHandleOnTabBackPress,
    resetFragmentTreeStates,
    setSelectedFragmentLayer,
  } = props;
  const [searchFragmentField, setSearchFragmentField] = useState('');
  const [filteredFragments, setFilteredFragments] = useState(
    [] as FragmentScreenInfoDTO[],
  );
  const [globalFragments, setGlobalFragments] = useState([] as FragmentScreenInfoDTO[]);
  const [crumbs, setCrumbs] = useState([]);
  const [selectedCrumbNode, setSelectedCrumbNode] = useState(null);
  const treeNodeId = useExpTreeNodeId();
  const _selectedLayerId = useSelectedLayerId();
  const expId = useExpId();
  const callPreserveUndoableState = usePreserveExperiment();
  const _selectedLayerDetail = useSelectedExpLayer()?.layerDetail as FragmentLayerDTO;
  const exp = useRMSelector(selectExp);
  const { treeControlState } = useLayersContainerContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);
  const treeNodeFragmentIds = useMemo(
    () => getTreeNodeIdsByType(treeControlState.treenodes, ETreeNodeTypes.FragmentScreen),
    [treeControlState.treenodes],
  );

  const loadFragments = useCallback(async (crumb) => {
    if (!crumb) {
      return;
    }
    setIsLoading(true);
    try {
      const response = await wizardService.getWizardFragments(crumb.id);
      if (response != null) {
        const parentList = await treeService.getDocumentParentNodes(
          crumb.id,
          crumb.type,
          true,
        );
        setCrumbs(parentList);
        setGlobalFragments(response);
      }
    } catch {
      return;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCrumbNode) {
      loadFragments(selectedCrumbNode);
    }
  }, [selectedCrumbNode, loadFragments]);

  useEffect(() => {
    if (!globalFragments) {
      setFilteredFragments([]);
      return;
    }

    setFilteredFragments(
      globalFragments.filter(
        (fragment) =>
          treeNodeFragmentIds.has(fragment.treeNodeId) &&
          fragment.fragmentScreenName
            .toLowerCase()
            .includes(searchFragmentField.toLowerCase()),
      ),
    );
  }, [globalFragments, searchFragmentField, treeNodeFragmentIds]);

  useEffect(() => {
    (async () => {
      if (!parentNode) {
        const _parentnode = new TreeNodeExpanded();
        await treeService.getParentNodeByTreeNodeId(treeNodeId).then((resp) => {
          _parentnode.id = resp.id;
          _parentnode.type = resp.type;
        });
        setSelectedCrumbNode(_parentnode);
      } else {
        setSelectedCrumbNode(parentNode);
      }
    })();
  }, []);

  useEffect(() => {
    setHandleOnTabBackPress(() => () => {
      handleBackToPreviousScreen(ELayersView.LAYERDESIGNVIEW)();
      resetFragmentTreeStates();
    });
  }, [setHandleOnTabBackPress]);

  useEffect(() => {
    if (!explorerBroadcastChannel) {
      return;
    }

    const handleBroadcastMessage = (messageEvent: MessageEvent<MessageType<any>>) => {
      const { action, payload } = messageEvent.data;

      if (action === CHANNEL_ACTIONS.EXPLORER_RENAME_NODE) {
        const node = payload as { id: number; name: string };
        if (!node?.id) {
          return;
        }
        setGlobalFragments((prev) =>
          prev.map((f) =>
            f.treeNodeId === node.id
              ? ({ ...f, fragmentScreenName: node.name } as FragmentScreenInfoDTO)
              : f,
          ),
        );
        return;
      }

      if (action === CHANNEL_ACTIONS.EXPLORER_DELETE_NODE) {
        const { deletedNodeIds } = payload as { deletedNodeIds?: number[] };
        if (!deletedNodeIds?.length) {
          return;
        }
        const deletedSet = new Set(deletedNodeIds);
        setGlobalFragments((prev) => prev.filter((f) => !deletedSet.has(f.treeNodeId)));
        return;
      }

      if (
        action === CHANNEL_ACTIONS.EXPLORER_ADD_NODE ||
        action === CHANNEL_ACTIONS.EXPLORER_RESTORE_NODE
      ) {
        loadFragments(selectedCrumbNode);
      }
    };

    explorerBroadcastChannel.onmessage = handleBroadcastMessage;

    return () => {
      explorerBroadcastChannel.onmessage = null;
    };
  }, [explorerBroadcastChannel, selectedCrumbNode, loadFragments]);

  const selectFragment = (fragment: FragmentScreenInfoDTO) => {
    const fragmentLayer = new FragmentLayerDTO();
    callPreserveUndoableState();
    fragmentLayer.layerId = _selectedLayerId;
    fragmentLayer.experimentId = expId;
    fragmentLayer.fragmentScreenId = fragment.fragmentScreenList[0]?.id;
    fragmentLayer.fragmentScreen = fragment.fragmentScreenList[0];
    fragmentLayer.id = _selectedLayerDetail.id;
    fragmentLayer.volume = exp.containerDefaultFragmentVolume;
    setSelectedFragmentLayer(fragmentLayer);
    setContentView(ELayersView.EDITFRAGMENT);
    handleBackToPreviousScreen(null);
  };

  const onChangeSearchedFragment = (e) => {
    const value = e.target.value;
    setSearchFragmentField(value);
  };

  const openFragmentTreeSidebar = () => {
    setContentView(ELayersView.FRAGMENTTREEPATH);
  };

  const handleCrumbClick = (crumb) => {
    setSelectedCrumbNode(crumb);
  };

  if (treeControlState?.isLoading) {
    return (
      <div className="m-4">
        <LoadingDiv />
      </div>
    );
  }

  return (
    <div className="select-ingredients-tabs">
      <div style={{ padding: '1.45% 4.36%' }}>
        <CustomBreadCrumbs
          isProteinControl={false}
          isScreenControl={false}
          treeNodes={undefined}
          breadCrumbs={crumbs}
          onBreadCrumbClick={handleCrumbClick}
          hideTreeNodes={true}
        />
      </div>
      <div className="flex flex-space-between" style={styles.clickBoxContainer}>
        <InputWithIcon
          icon={<RMSvgIcon name="Search" fill="#313131" />}
          onChange={onChangeSearchedFragment}
          value={searchFragmentField}
          placeholder={'Search here...'}
          containerStyle={styles.inputStyle}
          inputStyle={{ height: '40px' }}
        />
        <div style={{ padding: '1.45%' }}>
          <RMCustomButton
            iconProps={{ name: 'Folder', fill: treeNodeIconsColor }}
            onClick={openFragmentTreeSidebar}
          />
        </div>
      </div>
      <div></div>
      <div className="ingredients-container">
        {isLoading ? (
          <LoadingDiv />
        ) : (
          filteredFragments != undefined &&
          filteredFragments.map((fragment) => (
            <FragmentScreentItem
              text={fragment.fragmentScreenName}
              key={fragment.treeNodeId}
              onClick={() => selectFragment(fragment)}
              conditions={fragment?.fragmentScreenList[0]?.fragmentFragmentScreen?.length}
            />
          ))
        )}
      </div>
    </div>
  );
};

export const FragmentScreentItem = ({ text, onClick, conditions = 0 }) => {
  return (
    <div
      id="commandpanel-selectfragmentscreen-item-wrapper"
      style={styles.fragmentItemMainContainer}
      onClick={onClick}
    >
      <RMSvgIcon
        id="commandpanel-selectfragmentscreen-icon"
        name="FragmentScreen"
        fill={treeNodeIconsColor}
        style={styles.fragmentItemIcon}
      />
      <div
        id="commandpanel-selectfragmentscreen-item-content"
        className="flex flex-column"
      >
        <div
          id={`commandpanel-selectfragmentscreen-item-${text}`}
          style={styles.fragmentItemTitle}
          className="text-align-start item-text-flex-start"
        >
          {text}
        </div>
        <div
          id="commandpanel-selectfragmentscreen-item-conditions"
          style={styles.fragmentConditions}
        >{`${conditions} conditions`}</div>
      </div>
    </div>
  );
};

const styles: IStyle = {
  fragmentItemMainContainer: {
    display: 'flex',
    boxSizing: 'border-box',
    borderRadius: 4,
    width: '98%',
    padding: '4px 11px',
    marginTop: 5,
    alignItems: 'center',
    cursor: 'pointer',
  },
  fragmentItemIcon: {
    marginRight: 5,
  },
  fragmentItemTitle: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  fragmentConditions: {
    color: '#737373',
  },
  clickBoxContainer: {
    padding: '1.45% 4.36%',
  },
  inputStyle: {
    width: '100%',
  },
};
export default withTabBackButton(SelectFragmentScreen);
